import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Phone, MessageSquare, Mail, CalendarPlus, ExternalLink, Sparkles,
  Clock, User, Tag, Loader2, CheckCircle2, Undo2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { clientsAPI, retentionAPI } from '@/lib/api';

const KIND_STYLE = {
  renewal: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  cross_sell: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  needs_contact: 'bg-amber-50 text-amber-700 border-amber-200',
  at_risk: 'bg-rose-50 text-rose-700 border-rose-200',
};

function lastContactLabel(iso) {
  if (!iso) return 'Never contacted';
  const d = new Date(iso);
  const months = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
  if (months <= 0) return 'Contacted this month';
  return `Last contacted: ${months} mo ago`;
}

// Compact, action-oriented card for one retention opportunity.
export default function OpportunityCard({ opp, onAI, onWorked, onUndo, worked = false }) {
  const navigate = useNavigate();
  const [showSchedule, setShowSchedule] = useState(false);
  const [busy, setBusy] = useState(false);

  const call = () => {
    if (!opp.phone) { toast.error('No phone on file'); return; }
    if (window.krSoftphoneDial) {
      window.krSoftphoneDial(opp.phone, { contact_type: 'client', contact_id: opp.client_id });
    } else {
      window.location.href = `tel:${opp.phone}`;
    }
  };
  const text = () => navigate(`/clients/${opp.client_id}?tab=messages`);
  const email = () => {
    if (opp.email) window.location.href = `mailto:${opp.email}`;
    else navigate(`/clients/${opp.client_id}`);
  };

  const markWorked = async () => {
    setBusy(true);
    try {
      await retentionAPI.markWorked({
        opportunity_id: opp.id,
        client_id: opp.client_id,
        kind: opp.kind,
        category: opp.kind,
        cycle: opp.cycle,
        snapshot: opp,
      });
      toast.success('Marked as worked — hidden for this season');
      onWorked && onWorked(opp);
    } catch {
      toast.error('Could not mark as worked');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      await retentionAPI.undoWorked(opp.worked_id);
      toast.success('Moved back to the active list');
      onUndo && onUndo(opp);
    } catch {
      toast.error('Could not undo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 hover:shadow-sm transition-shadow" data-testid={`opp-card-${opp.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate(`/clients/${opp.client_id}`)}
              className="font-semibold text-slate-900 hover:text-teal-700 truncate"
              data-testid={`opp-name-${opp.id}`}
            >
              {opp.client_name}
            </button>
            {opp.product && (
              <Badge variant="outline" className="text-[10px] gap-1">
                <Tag size={9} /> {opp.product}{opp.coverage_year ? ` ${opp.coverage_year}` : ''}
              </Badge>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <Badge className={`text-[10px] border ${KIND_STYLE[opp.kind] || ''}`}>{opp.reason}</Badge>
            {(opp.carrier || opp.plan_name) && (
              <span className="text-xs text-slate-500">{[opp.carrier, opp.plan_name].filter(Boolean).join(' · ')}</span>
            )}
          </div>
          {opp.kind === 'cross_sell' && Array.isArray(opp.suggested_products) && (
            <div className="mt-1.5 flex items-center gap-1 flex-wrap" data-testid={`opp-suggested-${opp.id}`}>
              <span className="text-[11px] text-slate-400">Suggest:</span>
              {opp.suggested_products.map((p) => (
                <Badge key={p} variant="outline" className="text-[10px] bg-emerald-50/60">{p}</Badge>
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span className="flex items-center gap-1 text-slate-600 font-medium"><Clock size={11} /> {opp.due_period}</span>
            <span className="flex items-center gap-1">{lastContactLabel(opp.last_contact_at)}</span>
            {opp.assigned_agent_name && <span className="flex items-center gap-1"><User size={11} /> {opp.assigned_agent_name}</span>}
          </div>
        </div>
        <Button
          size="sm"
          className="shrink-0 bg-violet-600 hover:bg-violet-700 h-8"
          onClick={() => onAI(opp)}
          data-testid={`opp-ai-${opp.id}`}
        >
          <Sparkles size={13} className="mr-1" /> AI
        </Button>
      </div>

      {/* Actions — reuse existing calling / SMS / email / calendar */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 flex-wrap">
        {worked ? (
          <>
            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium mr-1">
              <CheckCircle2 size={13} /> Worked{opp.worked_by_name ? ` · ${opp.worked_by_name}` : ''}
            </span>
            <Button variant="outline" size="sm" className="h-8 text-xs shrink-0 ml-auto" onClick={undo} disabled={busy} data-testid={`opp-undo-${opp.id}`}>
              {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Undo2 size={13} className="mr-1" />} Undo
            </Button>
          </>
        ) : (
          <>
            <ActionBtn icon={<Phone size={13} />} label="Call" onClick={call} testid={`opp-call-${opp.id}`} />
            <ActionBtn icon={<MessageSquare size={13} />} label="Text" onClick={text} testid={`opp-text-${opp.id}`} />
            <ActionBtn icon={<Mail size={13} />} label="Email" onClick={email} testid={`opp-email-${opp.id}`} />
            <ActionBtn icon={<CalendarPlus size={13} />} label="Schedule" onClick={() => setShowSchedule(true)} testid={`opp-schedule-${opp.id}`} />
            <ActionBtn icon={<ExternalLink size={13} />} label="Open" onClick={() => navigate(`/clients/${opp.client_id}`)} testid={`opp-open-${opp.id}`} />
            <Button
              variant="outline" size="sm"
              className="h-8 text-xs shrink-0 ml-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              onClick={markWorked} disabled={busy}
              data-testid={`opp-worked-${opp.id}`}
            >
              {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <CheckCircle2 size={13} className="mr-1" />} Mark as Worked
            </Button>
          </>
        )}
      </div>

      <ScheduleReviewDialog
        open={showSchedule}
        onClose={() => setShowSchedule(false)}
        opp={opp}
      />
    </div>
  );
}

const ActionBtn = ({ icon, label, onClick, testid }) => (
  <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={onClick} data-testid={testid}>
    <span className="mr-1">{icon}</span> {label}
  </Button>
);

// Quick follow-up creation — reuses the existing client follow-up API
// (which syncs to Google Calendar).
function ScheduleReviewDialog({ open, onClose, opp }) {
  const [when, setWhen] = useState('');
  const [note, setNote] = useState(opp?.reason ? `Retention: ${opp.reason}` : 'Annual review');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!when) { toast.error('Pick a date & time'); return; }
    setSaving(true);
    try {
      await clientsAPI.createFollowUp(opp.client_id, new Date(when).toISOString(), note);
      toast.success('Review scheduled');
      onClose();
    } catch {
      toast.error('Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="schedule-review-dialog">
        <DialogHeader>
          <DialogTitle>Schedule review — {opp?.client_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">When</label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-9" data-testid="schedule-when" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Note</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-9" data-testid="schedule-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="schedule-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarPlus size={14} className="mr-1.5" />} Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
